import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { fetchContinuityReviews } from "@/lib/behavior/coachContinuityReviewService";
import type { VisibilityState } from "@/lib/behavior/viewModels/coachSessionPrepVeera";

export interface CoachLeaderCarryForwardState {
  id: string;
  coach_id: string;
  leader_id: string;
  note: string;
  visibility_state: VisibilityState;
  created_at: string;
  updated_at: string;
}

interface SaveCarryForwardStateInput {
  note: string;
  visibilityState: VisibilityState;
  expectedUpdatedAt?: string | null;
}

interface UseCoachLeaderCarryForwardOptions {
  readonly enabled?: boolean;
  readonly accessGeneration?: string;
  readonly relationshipRevision?: string | null;
}

const RELATIONSHIP_AUTHORIZATION_ERROR = "Relationship authorization required";

type CarryForwardTableClient = {
  select: (columns: string) => {
    eq: (column: string, value: string) => ReturnType<CarryForwardTableClient["select"]>;
    maybeSingle: () => Promise<{ data: CoachLeaderCarryForwardState | null; error: unknown }>;
  };
  upsert: (
    row: {
      coach_id: string;
      leader_id: string;
      note: string;
      visibility_state: VisibilityState;
    },
    options: { onConflict: string },
  ) => {
    select: () => {
      single: () => Promise<{ data: CoachLeaderCarryForwardState; error: unknown }>;
    };
  };
  update: (row: { note: string; visibility_state: VisibilityState }) => {
    eq: (column: string, value: string) => ReturnType<CarryForwardTableClient["update"]>;
    select: () => { single: () => Promise<{ data: CoachLeaderCarryForwardState; error: unknown }> };
    single: () => Promise<{ data: CoachLeaderCarryForwardState; error: unknown }>;
  };
  insert: (row: {
    coach_id: string;
    leader_id: string;
    note: string;
    visibility_state: VisibilityState;
  }) => {
    select: () => { single: () => Promise<{ data: CoachLeaderCarryForwardState; error: unknown }> };
  };
};

function getCarryForwardTable() {
  return supabase.from("coach_leader_carry_forward_notes" as never) as unknown as CarryForwardTableClient;
}

export function useCoachLeaderCarryForward(
  leaderId: string,
  { enabled = true, accessGeneration = "unknown", relationshipRevision = null }: UseCoachLeaderCarryForwardOptions = {},
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["coach-leader-carry-forward", user?.id, leaderId, accessGeneration, relationshipRevision];
  const canQuery = Boolean(enabled && user?.id && leaderId);

  const stateQuery = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) throw new Error("Not authenticated");
      try {
        const { data, error } = await getCarryForwardTable()
          .select("id, coach_id, leader_id, note, visibility_state, created_at, updated_at")
          .eq("coach_id", user.id)
          .eq("leader_id", leaderId)
          .maybeSingle();

        if (error) throw error;
        return data;
      } catch {
        console.warn("[useCoachLeaderCarryForward] carry_forward_note_read_failed");
        throw new Error("carry_forward_read_failed");
      }
    },
    enabled: canQuery,
  });

  const reviewQuery = useQuery({
    queryKey: [...queryKey, "cgcl-reviews"],
    queryFn: async () => {
      if (!user) throw new Error("Not authenticated");
      try {
        const { data, error } = await fetchContinuityReviews({
          supabase,
          coachId: user.id,
          leaderId,
          mode: "leader",
          reviewState: "carried_forward",
        });
        if (error) throw error;
        return data ?? [];
      } catch {
        console.warn("[useCoachLeaderCarryForward] carried_forward_reviews_read_failed");
        throw new Error("carry_forward_read_failed");
      }
    },
    enabled: canQuery,
  });

  const saveStateMutation = useMutation({
    mutationFn: async ({ note, visibilityState, expectedUpdatedAt }: SaveCarryForwardStateInput) => {
      if (!enabled) throw new Error(RELATIONSHIP_AUTHORIZATION_ERROR);
      if (!user) throw new Error("Not authenticated");
      const trimmedNote = note.trim();
      if (!trimmedNote) throw new Error("Carry-forward note is required");

      const table = getCarryForwardTable();
      const { data: current, error: currentError } = await table
        .select("id, coach_id, leader_id, note, visibility_state, created_at, updated_at")
        .eq("coach_id", user.id)
        .eq("leader_id", leaderId)
        .maybeSingle();
      if (currentError) {
        console.warn("[useCoachLeaderCarryForward] carry_forward_note_read_failed");
        throw new Error("carry_forward_read_failed");
      }
      if (expectedUpdatedAt !== undefined && current?.updated_at !== expectedUpdatedAt) {
        throw new Error("Carry-forward note changed. Review the latest private note before saving.");
      }

      const response = current
        ? await table.update({ note: trimmedNote, visibility_state: visibilityState })
          .eq("id", current.id)
          .eq("updated_at", current.updated_at)
          .select()
          .single()
        : await table.insert({
          coach_id: user.id,
          leader_id: leaderId,
          note: trimmedNote,
          visibility_state: visibilityState,
        }).select().single();
      const { data, error } = response;

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    state: canQuery && !stateQuery.isError ? stateQuery.data ?? null : null,
    reviewedCarryForward: canQuery && !reviewQuery.isError ? reviewQuery.data ?? [] : [],
    isLoading: canQuery ? stateQuery.isLoading || reviewQuery.isLoading : false,
    loadError: canQuery && (stateQuery.isError || reviewQuery.isError) ? "carry_forward_read_failed" : null,
    refreshState: async () => {
      if (!canQuery) return;
      await Promise.all([stateQuery.refetch(), reviewQuery.refetch()]);
    },
    saveState: saveStateMutation.mutateAsync,
    isSaving: saveStateMutation.isPending,
  };
}
